import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import SessionManager from '../utils/sessionManager';
import { API_BASE_URL, FALLBACK_API_URL } from '../config';

const PlacementCertificatePage = () => {
  const navigate = useNavigate();
  const { certificateId } = useParams();
  const [loading, setLoading] = useState(true);
  const [certificate, setCertificate] = useState(null);

  const session = SessionManager.getSession('candidate');
  const apiUrl = API_BASE_URL || FALLBACK_API_URL;

  useEffect(() => {
    if (!session) {
      navigate('/login');
      return;
    }
    loadCertificate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  const loadCertificate = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${apiUrl}/placement-training/certificates/${certificateId}`,
        {
          headers: {
            'Authorization': `Bearer ${session.access_token}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setCertificate(data.certificate);
      }
    } catch (error) {
      console.error('Error loading certificate:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    try {
      // Load libraries dynamically if not already loaded
      if (!window.html2canvas) {
        const html2canvasScript = document.createElement('script');
        html2canvasScript.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js';
        html2canvasScript.onload = async () => {
          if (!window.jspdf) {
            const jspdfScript = document.createElement('script');
            jspdfScript.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
            jspdfScript.onload = () => generateCertificatePDF();
            document.head.appendChild(jspdfScript);
          } else {
            generateCertificatePDF();
          }
        };
        document.head.appendChild(html2canvasScript);
      } else if (!window.jspdf) {
        const jspdfScript = document.createElement('script');
        jspdfScript.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
        jspdfScript.onload = () => generateCertificatePDF();
        document.head.appendChild(jspdfScript);
      } else {
        generateCertificatePDF();
      }
    } catch (error) {
      console.error('Error downloading certificate:', error);
      alert('Failed to download certificate. Please try again.');
    }
  };

  const generateCertificatePDF = async () => {
    try {
      const certificateElement = document.querySelector('.certificate-container');
      if (!certificateElement) {
        console.error('Certificate element not found');
        return;
      }

      // Capture the certificate as canvas
      const canvas = await window.html2canvas(certificateElement, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });

      const imgData = canvas.toDataURL('image/png');
      
      // Create PDF
      const { jsPDF } = window.jspdf;
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      
      // Add image to PDF (fit to page)
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);

      // Generate filename
      const fileName = `${certificate.candidate_name.toLowerCase().replace(/\s+/g, '_')}_certificate.pdf`;
      
      // Download the PDF
      pdf.save(fileName);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    }
  };

  const handleShare = () => {
    const text = `I've successfully completed the ${certificate.position_title} assessment at ${certificate.company_name} with a score of ${certificate.overall_score}%!`;
    const url = window.location.href;
    
    if (navigator.share) {
      navigator.share({
        title: 'Placement Training Certificate',
        text: text,
        url: url
      });
    } else {
      navigator.clipboard.writeText(`${text}\n${url}`);
      alert('Link copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-950 to-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  if (!certificate) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-950 to-black flex items-center justify-center">
        <div className="text-center py-12 text-white">
          <p className="text-xl">Certificate not found</p>
        </div>
      </div>
    );
  }

  const issueDate = new Date(certificate.issued_at);
  const getOverallGrade = (score) => {
    if (score >= 90) return 'EXCELLENT';
    if (score >= 80) return 'VERY GOOD';
    if (score >= 70) return 'GOOD';
    if (score >= 60) return 'SATISFACTORY';
    return 'PASS';
  };

  return (
    <>
      <style>{`
        .certificate-container {
          box-sizing: border-box;
        }
      `}</style>
      
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-950 to-black flex items-center justify-center p-5">
        {/* Action Buttons */}
        <div className="no-print fixed top-5 right-5 flex gap-3 z-50">
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-white text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium flex items-center gap-2"
          >
            ← Back
          </button>
          <button
            onClick={handleShare}
            className="px-4 py-2 bg-white text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium"
          >
            Share
          </button>
          <button
            onClick={handleDownload}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
          >
            Download PDF
          </button>
        </div>

        {/* Certificate */}
        <div className="certificate-container bg-white w-full max-w-[1000px] shadow-2xl relative" style={{
          aspectRatio: '1.414',
          border: '20px solid #0a1929',
          fontFamily: 'Georgia, Times New Roman, serif',
          padding: '50px'
        }}>
          {/* Decorative Corners */}
          <div className="absolute top-[40px] left-[40px] w-[80px] h-[80px] border-t-[3px] border-l-[3px]" style={{ borderColor: '#d4af37' }}></div>
          <div className="absolute top-[40px] right-[40px] w-[80px] h-[80px] border-t-[3px] border-r-[3px]" style={{ borderColor: '#d4af37' }}></div>
          <div className="absolute bottom-[40px] left-[40px] w-[80px] h-[80px] border-b-[3px] border-l-[3px]" style={{ borderColor: '#d4af37' }}></div>
          <div className="absolute bottom-[40px] right-[40px] w-[80px] h-[80px] border-b-[3px] border-r-[3px]" style={{ borderColor: '#d4af37' }}></div>

          {/* Header */}
          <div className="text-center mb-6">
            <div className="text-4xl font-bold mb-1" style={{ color: '#0a1929', letterSpacing: '4px' }}>APTIRIX</div>
            <div className="text-xs text-gray-600 uppercase" style={{ letterSpacing: '2px' }}>India's First AI-Powered Career Platform</div>
          </div>

          {/* Certificate Title */}
          <div className="text-center my-5">
            <div className="text-[36px] font-bold uppercase mb-1" style={{ color: '#0a1929', letterSpacing: '3px' }}>Certificate of Achievement</div>
            <div className="text-base italic text-gray-600">MNC Placement Training Program</div>
          </div>

          {/* Content */}
          <div className="text-center my-6">
            <div className="text-sm text-gray-600 mb-3" style={{ letterSpacing: '1px' }}>This is to certify that</div>
            
            <div className="text-4xl font-bold my-4 inline-block pb-2" style={{ 
              color: '#0a1929',
              borderBottom: '2px solid #d4af37'
            }}>
              {certificate.candidate_name}
            </div>
            
            <div className="text-sm text-gray-700 leading-relaxed my-5 max-w-[700px] mx-auto">
              has successfully completed the comprehensive MNC Placement Training Program, 
              demonstrating exceptional skills in Aptitude, Coding, Technical Interview, 
              and HR Interview rounds. This achievement reflects dedication, competence, 
              and readiness for professional opportunities in leading organizations.
            </div>

            {/* Scores Section */}
            <div className="flex justify-center gap-8 my-6 flex-wrap">
              {certificate.aptitude_score && (
                <div className="text-center">
                  <div className="text-xs text-gray-600 uppercase mb-1" style={{ letterSpacing: '1px' }}>Aptitude</div>
                  <div className="text-[28px] font-bold" style={{ color: '#0a1929' }}>{certificate.aptitude_score.toFixed(0)}%</div>
                </div>
              )}
              {certificate.coding_score && (
                <div className="text-center">
                  <div className="text-xs text-gray-600 uppercase mb-1" style={{ letterSpacing: '1px' }}>Coding</div>
                  <div className="text-[28px] font-bold" style={{ color: '#0a1929' }}>{certificate.coding_score.toFixed(0)}%</div>
                </div>
              )}
              {certificate.tech_interview_score && (
                <div className="text-center">
                  <div className="text-xs text-gray-600 uppercase mb-1" style={{ letterSpacing: '1px' }}>Technical</div>
                  <div className="text-[28px] font-bold" style={{ color: '#0a1929' }}>{certificate.tech_interview_score.toFixed(0)}%</div>
                </div>
              )}
              {certificate.hr_interview_score && (
                <div className="text-center">
                  <div className="text-xs text-gray-600 uppercase mb-1" style={{ letterSpacing: '1px' }}>HR Interview</div>
                  <div className="text-[28px] font-bold" style={{ color: '#0a1929' }}>{certificate.hr_interview_score.toFixed(0)}%</div>
                </div>
              )}
            </div>

            <div className="inline-block px-5 py-2 bg-gradient-to-r from-indigo-600 to-purple-700 text-white rounded-full text-sm font-bold mt-2">
              Overall Score: {certificate.overall_score.toFixed(0)}% - {getOverallGrade(certificate.overall_score)}
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-between items-end mt-[40px] pt-6 border-t-2 border-gray-300">
            <div className="text-center flex-1">
              <img src="/signatures/naveen-signature.svg" alt="Naveen Signature" className="w-[180px] h-[70px] mx-auto mb-1" />
              <div className="text-sm font-bold mb-1" style={{ color: '#0a1929' }}>Naveen Dhanasekaran</div>
              <div className="text-xs text-gray-600 italic">CEO & Founder</div>
            </div>

            <div className="text-center flex-1">
              <img src="/signatures/rahul-signature.svg" alt="Rahul Signature" className="w-[180px] h-[70px] mx-auto mb-1" />
              <div className="text-sm font-bold mb-1" style={{ color: '#0a1929' }}>Rahul V</div>
              <div className="text-xs text-gray-600 italic">Co-Founder & CTO</div>
            </div>
          </div>

          {/* Certificate Meta */}
          <div className="text-center mt-5">
            <div className="text-[11px] text-gray-500 my-1">
              Certificate No: <span className="font-bold" style={{ color: '#0a1929', fontFamily: 'Courier New, monospace' }}>{certificate.certificate_number}</span>
            </div>
            <div className="text-[11px] text-gray-500 my-1">
              Issue Date: {issueDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </div>
            <div className="text-[11px] text-gray-500 my-1">
              Company: {certificate.company_name} | Position: {certificate.position_title}
            </div>
            <div className="text-[11px] text-gray-500 my-1">
              Verify at: aptirix.com/verify/<span className="font-bold" style={{ color: '#0a1929', fontFamily: 'Courier New, monospace' }}>{certificate.verification_code}</span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PlacementCertificatePage;
